import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Checkbox,
  Toolbar,
  Typography,
  Card,
  CardContent,
  Grid,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  CheckCircle as ActivateIcon,
  Cancel as DeactivateIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchOperators,
  fetchSupervisors,
  fetchOperatorsStatistics,
  deleteOperator,
  setSearchTerm,
  setFilters,
  setPagination,
  setSelectedOperator,
  setDialogOpen,
  setEditing,
  setSelectedOperatorIds,
  toggleOperatorSelection,
  selectAllOperators,
  deselectAllOperators,
  bulkActivateOperators,
  bulkDeactivateOperators,
} from '../../store/reducers/operatorsSlice';
import { Operator } from '../../services/operatorsApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  searchBox: {
    marginBottom: theme.spacing(2),
  },
  filtersBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  spacer: {
    flex: '1 1 100%',
  },
  actions: {
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  inactiveChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

const OperatorsList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.operators);

  const operators: Operator[] = Array.isArray(state?.operators) ? state!.operators : [];
  const supervisors = Array.isArray(state?.supervisors) ? state!.supervisors : [];
  const loading = !!state?.loading;
  const error = state?.error || null;
  const searchTerm = state?.searchTerm || '';
  const filters = state?.filters || { active: 'all', supervisorId: null };
  const pagination = state?.pagination || { page: 1, pageSize: 10 };
  const statistics = state?.statistics || { total: 0, active: 0, inactive: 0 };
  const selectedOperatorIds = Array.isArray(state?.selectedOperatorIds) ? state!.selectedOperatorIds : [];
  const bulkLoading = !!state?.bulkLoading;

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchOperators());
    dispatch(fetchSupervisors());
    dispatch(fetchOperatorsStatistics());
  }, [dispatch]);

  // Filter and search operators
  const filteredOperators = useMemo(() => {
    let filtered = operators;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(operator =>
        operator.Name.toLowerCase().includes(searchLower) ||
        operator.EmployeeNumber.toLowerCase().includes(searchLower) ||
        (`${operator.Supervisor?.FirstName || ''} ${operator.Supervisor?.LastName || ''}`.trim()).toLowerCase().includes(searchLower)
      );
    }

    if (filters.active && filters.active !== 'all') {
      filtered = filtered.filter(operator => operator.Active === (filters.active === 'true'));
    }

    if (filters.supervisorId) {
      filtered = filtered.filter(operator => operator.SupervisorId === filters.supervisorId);
    }

    return filtered;
  }, [operators, searchTerm, filters]);

  // Pagination
  const paginatedOperators = useMemo(() => {
    const startIndex = Math.max(0, ((pagination?.page || 1) - 1) * (pagination?.pageSize || 10));
    return filteredOperators.slice(startIndex, startIndex + (pagination?.pageSize || 10));
  }, [filteredOperators, pagination?.page, pagination?.pageSize]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleFilterChange = (filterName: string, value: any) => {
    dispatch(setFilters({ [filterName]: value }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPagination({ page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPagination({ 
      pageSize: parseInt(event.target.value, 10),
      page: 1 
    }));
  };

  const handleAddOperator = () => {
    dispatch(setSelectedOperator(null));
    dispatch(setEditing(false));
    dispatch(setDialogOpen(true));
  };

  const handleEditOperator = (operator: Operator) => {
    dispatch(setSelectedOperator(operator));
    dispatch(setEditing(true));
    dispatch(setDialogOpen(true));
  };

  const handleDeleteOperator = (id: number) => {
    if (window.confirm('Are you sure you want to delete this operator?')) {
      dispatch(deleteOperator(id));
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      dispatch(selectAllOperators());
    } else {
      dispatch(deselectAllOperators());
    }
  };

  const handleSelectOperator = (operatorId: number) => {
    dispatch(toggleOperatorSelection(operatorId));
  };

  const handleBulkActivate = () => {
    if (selectedOperatorIds.length > 0) {
      dispatch(bulkActivateOperators(selectedOperatorIds));
    }
  };

  const handleBulkDeactivate = () => {
    if (selectedOperatorIds.length > 0) {
      dispatch(bulkDeactivateOperators(selectedOperatorIds));
    }
  };

  const isSelected = (operatorId: number) => selectedOperatorIds.includes(operatorId);
  const numSelected = selectedOperatorIds.length;

  if (loading && operators.length === 0) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue}>
                {statistics.total}
              </Typography>
              <Typography className={classes.statLabel}>
                Total Operators
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue} style={{ color: '#4caf50' }}>
                {statistics.active}
              </Typography>
              <Typography className={classes.statLabel}>
                Active Operators
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue} style={{ color: '#f44336' }}>
                {statistics.inactive}
              </Typography>
              <Typography className={classes.statLabel}>
                Inactive Operators
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Box className={classes.searchBox}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search operators by name, employee number, or supervisor..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon color="action" />,
          }}
        />
      </Box>

      {/* Filters */}
      <Box className={classes.filtersBox}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="subtitle2">
              <FilterIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Filters:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.active}
                onChange={(e) => handleFilterChange('active', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Supervisor</InputLabel>
              <Select
                value={filters.supervisorId || ''}
                onChange={(e) => handleFilterChange('supervisorId', e.target.value || null)}
                label="Supervisor"
              >
                <MenuItem value="">All Supervisors</MenuItem>
                {supervisors.map((supervisor) => (
                  <MenuItem key={supervisor.Id} value={supervisor.Id}>
                    {supervisor.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Toolbar */}
      <Toolbar className={classes.toolbar}>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Typography variant="h6">
            Operators ({filteredOperators.length})
          </Typography>
        )}
        <div className={classes.spacer} />
        <div className={classes.actions}>
          {numSelected > 0 ? (
            <>
              <Tooltip title="Activate Selected">
                <IconButton 
                  onClick={handleBulkActivate}
                  disabled={bulkLoading}
                >
                  <ActivateIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Deactivate Selected">
                <IconButton 
                  onClick={handleBulkDeactivate}
                  disabled={bulkLoading}
                >
                  <DeactivateIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title="Export">
                <IconButton>
                  <ExportIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddOperator}
              >
                Add New Operator
              </Button>
            </>
          )}
        </div>
      </Toolbar>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < filteredOperators.length}
                  checked={filteredOperators.length > 0 && numSelected === filteredOperators.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Employee Number</TableCell>
              <TableCell>Supervisor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOperators.map((operator) => {
              const isItemSelected = isSelected(operator.Id);
              return (
                <TableRow
                  key={operator.Id}
                  hover
                  selected={isItemSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isItemSelected}
                      onChange={() => handleSelectOperator(operator.Id)}
                    />
                  </TableCell>
                  <TableCell>{operator.Name}</TableCell>
                  <TableCell>{operator.EmployeeNumber}</TableCell>
                  <TableCell>
                    {operator.Supervisor 
                      ? `${operator.Supervisor.FirstName} ${operator.Supervisor.LastName}`
                      : 'No Supervisor'
                    }
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={operator.Active ? 'Active' : 'Inactive'}
                      className={operator.Active ? classes.activeChip : classes.inactiveChip}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small"
                        onClick={() => handleEditOperator(operator)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteOperator(operator.Id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredOperators.length}
        rowsPerPage={pagination.pageSize}
        page={pagination.page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Box>
  );
};

export default OperatorsList; 